/* import __COLOCATED_TEMPLATE__ from './modal.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import type IntlService from 'ember-intl/services/intl';

import type Attribute from 'embercom/models/attribute';
import type ConversationAttributeDescriptor from 'embercom/models/conversation-attributes/descriptor';
import { action } from '@ember/object';
import type ListOption from 'embercom/models/conversation-attributes/list-option';
import { post, put } from 'embercom/lib/ajax';
import {
  type FieldType,
  type TicketField,
  type UserField,
} from 'embercom/objects/standalone/data-fields/common';
import type Store from '@ember-data/store';
import type ApplicationInstance from '@ember/application/instance';
import type ExternalFieldIdentifier from 'embercom/models/standalone/external-field-identifier';
import type { ATTRIBUTE_DESCRIPTOR_TYPES } from 'embercom/models/custom-objects/constants/attribute-descriptor-types';
import type RouterService from '@ember/routing/router-service';

interface Args {
  field: UserField | TicketField | ExternalFieldIdentifier;
  onClose: () => void;
}

const FIELD_TYPE_MAPPING: Record<FieldType | ATTRIBUTE_DESCRIPTOR_TYPES, string> = {
  text: 'text',
  textarea: 'text',
  checkbox: 'boolean',
  date: 'date',
  integer: 'number',
  decimal: 'decimal',
  regexp: 'text',
  dropdown: 'list',
  multiselect: 'list',
  lookup: 'relationship',
  tagger: 'list',
  id: 'string',
  string: 'string',
  address: 'string',
  email: 'string',
  phone: 'string',
  boolean: 'boolean',
  datetime: 'date',
  url: 'string',
  double: 'number',
  picklist: 'list',
  priority: 'list',
  tickettype: 'list',
  fixnum: 'number',
  float: 'number',
  options: 'list',
  list: 'list',
  relationship: 'relationship',
  files: 'list',
};

export default class DataFieldModal extends Component<Args> {
  @service declare finStandaloneService: FinStandaloneService;
  @service declare appService: {
    app: { id: string; currentAdmin: { id: string }; reload: () => void };
  };
  @service declare router: RouterService;
  @service declare store: Store;
  @service declare notificationsService: {
    notifyConfirmation: (message: string) => void;
    notifyResponseError: (
      e: Error,
      messages: Record<string, string> & { default: string },
      options?: { responseProvidedErrors: number[] },
    ) => void;
  };
  @service declare intl: IntlService;

  get fieldType() {
    return this.intl.t(`standalone.data-field.modal.format.types.${this.fieldTypeMapping}`);
  }

  get fieldTypeMapping() {
    return FIELD_TYPE_MAPPING[this.args.field.type];
  }

  conversationAttribute: ConversationAttributeDescriptor | undefined;
  userAttribute: Attribute | undefined;

  constructor(owner: ApplicationInstance, args: Args) {
    super(owner, args);

    if ('intercom_cvda' in this.args.field) {
      if (this.args.field.intercom_cvda) {
        this.conversationAttribute = this.store.push(
          this.store.normalize('conversation-attributes/descriptor', this.args.field.intercom_cvda),
        ) as unknown as ConversationAttributeDescriptor;
      }
    }

    if ('intercom_cda' in this.args.field) {
      if (this.args.field.intercom_cda) {
        this.userAttribute = this.store.push(
          this.store.normalize('attribute', this.args.field.intercom_cda),
        ) as Attribute;
      }
    }

    if ('attributeModel' in this.args.field) {
      this.userAttribute = this.args.field.attributeModel;
    }
  }

  get app() {
    return this.appService.app;
  }

  @action async saveConversationAttribute() {
    if (this.conversationAttribute) {
      try {
        if (
          this.conversationAttribute.listOptions.every((option: ListOption) => option.description)
        ) {
          this.conversationAttribute.isClassificationAttribute = true;
        }

        await this.conversationAttribute.save();

        // @ts-expect-error we know this is a ticket field
        this.args.field.intercom_cvda = this.conversationAttribute.serialize();

        this.args.onClose();
      } catch (error) {
        this.notificationsService.notifyResponseError(error, {
          default: this.intl.t('standalone.data-field.modal.errors.save'),
        });
      }
    }
  }

  @action async syncField() {
    let url;
    if (this.userAttribute && this.userAttribute.isNotCompany) {
      url = `/ember/standalone/zendesk_data/${this.args.field.id}/synchronize_user_field`;
    } else if (this.userAttribute && this.userAttribute.isCompany) {
      url = `/ember/standalone/zendesk_data/${this.args.field.id}/synchronize_organization_field`;
    } else if (this.conversationAttribute) {
      url = `/ember/standalone/zendesk_data/${this.args.field.id}/synchronize_ticket_field`;
    }

    if (!url) {
      return;
    }

    try {
      await post(url, { app_id: this.app.id, admin_id: this.app.currentAdmin.id });

      this.notificationsService.notifyConfirmation(
        this.intl.t('standalone.data-field.modal.notifications.field-sync-finished'),
      );
      this.app.reload();
    } catch (error) {
      this.notificationsService.notifyResponseError(error, {
        default: this.intl.t('standalone.data-field.modal.errors.sync-fields'),
      });
    }
  }

  @action async syncTags() {
    try {
      await post('/ember/standalone/zendesk_data/synchronize_tags', {
        app_id: this.app.id,
      });

      this.notificationsService.notifyConfirmation(
        this.intl.t('standalone.data-field.modal.notifications.tags-sync-started'),
      );
    } catch (error) {
      this.notificationsService.notifyResponseError(error, {
        default: this.intl.t('standalone.data-field.modal.errors.sync-tags'),
      });
    }
  }

  @action async remove() {
    if (this.userAttribute) {
      try {
        await put('/ember/data_attribute_settings/archive', {
          app_id: this.app.id,
          id: this.userAttribute.cdaId,
        });

        (this.args.field as UserField).intercom_cda = undefined;

        if ('synchronized' in this.args.field) {
          this.args.field.synchronized = false;
        }

        this.args.onClose();
      } catch (error) {
        this.notificationsService.notifyResponseError(error, {
          default: this.intl.t('standalone.data-field.modal.errors.remove'),
          412: this.intl.t('standalone.data-field.modal.errors.in-use'),
        });
      }
    } else if (this.conversationAttribute) {
      try {
        this.conversationAttribute.archived = true;
        this.conversationAttribute.order = 0;
        await this.conversationAttribute.save();

        // @ts-expect-error we know this is a ticket field
        this.args.field.intercom_cvda = undefined;

        this.args.onClose();
      } catch (error) {
        this.notificationsService.notifyResponseError(error, {
          default: this.intl.t('standalone.data-field.modal.errors.remove'),
          412: this.intl.t('standalone.data-field.modal.errors.in-use'),
        });
      }
    }
  }

  @action async convertToAICategory() {
    let attribute = this.conversationAttribute;

    if (attribute) {
      try {
        attribute.isClassificationAttribute = true;
        await attribute.save();
        this.notificationsService.notifyConfirmation(
          this.intl.t('standalone.data-field.modal.notifications.convert-to-category'),
        );
        this.router.transitionTo('apps.app.standalone.categories');
      } catch (error) {
        this.notificationsService.notifyResponseError(error, {
          default: this.intl.t('standalone.data-field.modal.errors.convert-to-category'),
        });
      }
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::DataField::Modal': typeof DataFieldModal;
    'standalone/data-field/modal': typeof DataFieldModal;
  }
}
